import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiFile,
  FiFolder,
  FiDownload,
  FiTrash2,
  FiEdit,
  FiCheckCircle,
  FiAlertCircle,
  FiXCircle,
  FiInfo,
  FiUpload,
  FiX,
  FiRotateCw
} from 'react-icons/fi';

const FileExplorer = ({
  files,
  selectedFile,
  onFileSelect,
  onFileEdit,
  onFileDownload,
  onFileDelete,
  onRefresh,
  isRefreshing = false
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [extensionFilter, setExtensionFilter] = useState('all');
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Diagnostic only: validates icon import assumptions before applying a fix
    if (typeof FiX === 'undefined') {
      console.error(
        '[FileExplorer] Diagnostic: FiX is undefined. The close icon in preview panel is likely missing an import from react-icons/fi.'
      );
    }
  }, []);

  const handleFileClick = async (file) => {
    console.debug('[FileExplorer.handleFileClick] click', {
      name: file?.name,
      extension: file?.extension,
      path: file?.path,
      hasInlineContent: file?.content !== undefined,
      source: file?.content !== undefined ? 'local-upload' : 'service-fetch'
    });

    onFileSelect(file);
    setShowPreview(true);
  };

  const handleDirectoryUpload = async (event) => {
    const files = Array.from(event.target.files);
    setIsLoading(true);
    
    try {
      const fileItems = [];
      
      for (const file of files) {
        const content = await readFileContent(file);
        const extension = file.name.includes('.') ?
          file.name.substring(file.name.lastIndexOf('.')) : '';
        
        fileItems.push({
          name: file.name,
          path: URL.createObjectURL(file),
          type: 'file',
          size: file.size,
          lastModified: new Date(file.lastModified),
          extension,
          validationStatus: 'unvalidated',
          validationErrors: [],
          content: extension === '.json' ? JSON.parse(content) : content
        });
      }
      
      setLocalFiles(fileItems);
    } catch (error) {
      console.error('Error processing files:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const allFiles = [...localFiles, ...files];

  const getNormalizedExtension = (file) => {
    if (typeof file?.extension === 'string' && file.extension.trim() !== '') {
      return file.extension.toLowerCase();
    }

    if (typeof file?.name === 'string' && file.name.includes('.')) {
      return file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    }

    return '';
  };

  const isPdfFile = (file) => {
    if (!file) return false;

    const extension = getNormalizedExtension(file);
    if (extension === '.pdf') {
      return true;
    }

    return typeof file?.mimeType === 'string' && file.mimeType.toLowerCase() === 'application/pdf';
  };

  const extensionOptions = Array.from(
    new Set(allFiles.map((file) => getNormalizedExtension(file)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const normalizedNameFilter = nameFilter.trim().toLowerCase();
  const filteredFiles = allFiles.filter((file) => {
    const matchesName =
      normalizedNameFilter.length === 0 ||
      (file?.name || '').toLowerCase().includes(normalizedNameFilter);
    const matchesExtension =
      extensionFilter === 'all' || getNormalizedExtension(file) === extensionFilter;

    return matchesName && matchesExtension;
  });

  const isLocalFile = (file) => typeof file?.path === 'string' && file.path.startsWith('blob:');

  const handleDownloadClick = async () => {
    if (!selectedFile || !onFileDownload) return;
    await onFileDownload(selectedFile);
  };

  const handleDeleteClick = async () => {
    if (!selectedFile) return;

    const shouldDelete = window.confirm(`Delete ${selectedFile.name}?`);
    if (!shouldDelete) return;

    if (isLocalFile(selectedFile)) {
      setLocalFiles((prev) => prev.filter((file) => file.path !== selectedFile.path));
      URL.revokeObjectURL(selectedFile.path);
      onFileSelect(null);
      setShowPreview(false);
      return;
    }

    if (onFileDelete) {
      const deleted = await onFileDelete(selectedFile);
      if (deleted) {
        setShowPreview(false);
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <FiCheckCircle className="w-4 h-4 text-success-500" />;
      case 'invalid':
        return <FiXCircle className="w-4 h-4 text-error-500" />;
      case 'warning':
        return <FiAlertCircle className="w-4 h-4 text-warning-500" />;
      default:
        return <FiInfo className="w-4 h-4 text-secondary-400" />;
    }
  };

  const getFileIcon = (file) => {
    if (file.type === 'directory') {
      return <FiFolder className="w-5 h-5 text-blue-500" />;
    }
    
    switch (file.extension) {
      case '.json':
        return <FiFile className="w-5 h-5 text-yellow-500" />;
      case '.txt':
      case '.tsv':
        return <FiFile className="w-5 h-5 text-blue-500" />;
      case '.png':
      case '.jpg':
      case '.jpeg':
        return <FiFile className="w-5 h-5 text-green-500" />;
      case '.glb':
        return <FiFile className="w-5 h-5 text-purple-500" />;
      default:
        return <FiFile className="w-5 h-5 text-secondary-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const renderFileContent = () => {
    // Use content from selectedFile prop, which is updated by App.tsx
    const fileContent = selectedFile?.content;
    
    if (!fileContent) {
      return (
        <div className="text-center text-secondary-500 py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p>Loading file content...</p>
        </div>
      );
    }
    
    // Handle image files first (check before string check since blob URLs are strings)
    if (selectedFile?.extension === '.png' || selectedFile?.extension === '.jpg' || selectedFile?.extension === '.jpeg') {
      return (
        <div className="text-center py-4">
          <img
            src={fileContent}
            alt={selectedFile.name}
            className="max-w-full max-h-96 mx-auto object-contain"
          />
        </div>
      );
    }
    
    // Handle 3D model files
    if (selectedFile?.extension === '.glb') {
      return (
        <div className="text-center text-secondary-500 py-8">
          <FiInfo className="w-8 h-8 mx-auto mb-2" />
          <p>3D model preview not available</p>
          <p className="text-xs mt-2">File: {selectedFile.name}</p>
        </div>
      );
    }

    // Render PDFs inline instead of showing their blob/data URL as plain text.
    if (isPdfFile(selectedFile)) {
      if (typeof fileContent === 'string' && (fileContent.startsWith('blob:') || fileContent.startsWith('data:') || /^https?:\/\//i.test(fileContent))) {
        return (
          <div className="space-y-3">
            <iframe
              src={fileContent}
              title={`${selectedFile.name} preview`}
              className="w-full h-[70vh] border border-secondary-200 rounded-lg bg-white"
            />
            <div className="text-xs text-secondary-500">
              If the PDF does not render, use the download button below.
            </div>
          </div>
        );
      }

      return (
        <div className="text-center text-secondary-500 py-8">
          <FiInfo className="w-8 h-8 mx-auto mb-2" />
          <p>PDF preview is unavailable for this content format.</p>
        </div>
      );
    }

    // Handle JSON files
    if (selectedFile?.extension === '.json') {
      return (
        <pre className="text-xs bg-secondary-50 p-4 rounded-lg">
          {JSON.stringify(fileContent, null, 2)}
        </pre>
      );
    }
    
    // Handle TSV/array data
    if (Array.isArray(fileContent)) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-secondary-50">
              <tr>
                {Object.keys(fileContent[0] || {}).map(key => (
                  <th key={key} className="px-2 py-1 text-left font-medium text-secondary-700">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fileContent.slice(0, 10).map((row, index) => (
                <tr key={index} className="border-t border-secondary-100">
                  {Object.values(row).map((value, i) => (
                    <td key={i} className="px-2 py-1 text-secondary-600">
                      {String(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {fileContent.length > 10 && (
            <p className="text-xs text-secondary-500 mt-2 text-center">
              ... and {fileContent.length - 10} more rows
            </p>
          )}
        </div>
      );
    }
    
    // Handle Markdown files with structured rendering (including tables)
    if (selectedFile?.extension === '.md') {
      const escapeHtml = (text = '') =>
        text
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const renderInlineMarkdown = (text = '') =>
        escapeHtml(text)
          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.+?)\*/g, '<em>$1</em>')
          .replace(/`(.+?)`/g, '<code>$1</code>')
          .replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
          );

      const parseTableRow = (row = '') =>
        row
          .trim()
          .replace(/^\|/, '')
          .replace(/\|$/, '')
          .split('|')
          .map((cell) => cell.trim());

      const tableSeparatorRegex = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/;

      const lines = fileContent.split('\n');
      const blocks = [];

      let i = 0;
      while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        if (!trimmed) {
          i += 1;
          continue;
        }

        const nextLine = lines[i + 1]?.trim() || '';
        if (line.includes('|') && tableSeparatorRegex.test(nextLine)) {
          const headers = parseTableRow(line);
          const alignmentCells = parseTableRow(nextLine);
          const alignments = headers.map((_, index) => {
            const token = (alignmentCells[index] || '').trim();
            if (token.startsWith(':') && token.endsWith(':')) return 'center';
            if (token.endsWith(':')) return 'right';
            return 'left';
          });

          i += 2;
          const rows = [];

          while (i < lines.length) {
            const rowLine = lines[i];
            if (!rowLine.trim() || !rowLine.includes('|')) break;
            rows.push(parseTableRow(rowLine));
            i += 1;
          }

          const headerHtml = headers
            .map(
              (header, index) =>
                `<th style="text-align:${alignments[index]};">${renderInlineMarkdown(header)}</th>`
            )
            .join('');

          const bodyHtml = rows
            .map((row) => {
              const cells = headers
                .map(
                  (_, index) =>
                    `<td style="text-align:${alignments[index]};">${renderInlineMarkdown(row[index] || '')}</td>`
                )
                .join('');
              return `<tr>${cells}</tr>`;
            })
            .join('');

          blocks.push(
            `<div class="md-table-wrapper"><table class="md-table"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`
          );
          continue;
        }

        if (/^###\s+/.test(trimmed)) {
          blocks.push(`<h3>${renderInlineMarkdown(trimmed.replace(/^###\s+/, ''))}</h3>`);
          i += 1;
          continue;
        }

        if (/^##\s+/.test(trimmed)) {
          blocks.push(`<h2>${renderInlineMarkdown(trimmed.replace(/^##\s+/, ''))}</h2>`);
          i += 1;
          continue;
        }

        if (/^#\s+/.test(trimmed)) {
          blocks.push(`<h1>${renderInlineMarkdown(trimmed.replace(/^#\s+/, ''))}</h1>`);
          i += 1;
          continue;
        }

        if (/^-\s+/.test(trimmed)) {
          const items = [];
          while (i < lines.length && /^-\s+/.test(lines[i].trim())) {
            items.push(lines[i].trim().replace(/^-\s+/, ''));
            i += 1;
          }

          blocks.push(`<ul>${items.map((item) => `<li>${renderInlineMarkdown(item)}</li>`).join('')}</ul>`);
          continue;
        }

        if (/^>\s+/.test(trimmed)) {
          const quoteLines = [];
          while (i < lines.length && /^>\s+/.test(lines[i].trim())) {
            quoteLines.push(lines[i].trim().replace(/^>\s+/, ''));
            i += 1;
          }

          blocks.push(`<blockquote>${quoteLines.map((q) => renderInlineMarkdown(q)).join('<br>')}</blockquote>`);
          continue;
        }

        const paragraphLines = [trimmed];
        i += 1;

        while (i < lines.length) {
          const current = lines[i];
          const currentTrimmed = current.trim();
          const following = lines[i + 1]?.trim() || '';

          const startsNewBlock =
            !currentTrimmed ||
            /^#{1,3}\s+/.test(currentTrimmed) ||
            /^-\s+/.test(currentTrimmed) ||
            /^>\s+/.test(currentTrimmed) ||
            (current.includes('|') && tableSeparatorRegex.test(following));

          if (startsNewBlock) break;

          paragraphLines.push(currentTrimmed);
          i += 1;
        }

        blocks.push(`<p>${renderInlineMarkdown(paragraphLines.join(' '))}</p>`);
      }

      const html = blocks.join('') || '<p></p>';
      
      return (
        <div className="markdown-preview max-w-none p-4 text-sm text-gray-800">
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      );
    }
    
    // Handle text content (including .txt and other text files)
    if (typeof fileContent === 'string') {
      return (
        <pre className="text-xs bg-secondary-50 p-4 rounded-lg whitespace-pre-wrap">
          {fileContent}
        </pre>
      );
    }
    
    return (
      <div className="text-center text-secondary-500 py-8">
        <FiInfo className="w-8 h-8 mx-auto mb-2" />
        <p>Preview not available for this file type</p>
      </div>
    );
  };

  return (
    <div className="h-full flex">
      {/* File List */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex-shrink-0 mb-6">
          <h2 className="text-2xl font-bold text-secondary-800 mb-2">File Explorer</h2>
          <p className="text-secondary-600">Browse and manage your inspection data files</p>
        </div>

        {/* Directory Upload */}
        <div className="flex-shrink-0 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-soft border border-secondary-100 p-6"
          >
            <h3 className="font-semibold text-secondary-800 mb-4">Load Data Directory</h3>
            <div className="flex items-center space-x-4">
              <input
                ref={fileInputRef}
                type="file"
                webkitdirectory="true"
                className="hidden"
                onChange={handleDirectoryUpload}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => fileInputRef.current?.click()}
                className="btn-primary flex items-center space-x-2"
                disabled={isLoading}
              >
                <FiUpload />
                <span>{isLoading ? 'Loading...' : 'Select Data Directory'}</span>
              </motion.button>
              {localFiles.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setLocalFiles([])}
                  className="btn-outline"
                >
                  Clear Local Files
                </motion.button>
              )}
            </div>
            {localFiles.length > 0 && (
              <div className="mt-4 text-sm text-secondary-600">
                Loaded {localFiles.length} files from local directory
              </div>
            )}
          </motion.div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl shadow-soft border border-secondary-100 overflow-hidden">
          <div className="flex-shrink-0 px-6 py-4 border-b border-secondary-200 bg-secondary-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-secondary-800">Data Files</h3>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-secondary-500">
                  {filteredFiles.length} of {allFiles.length} {allFiles.length === 1 ? 'file' : 'files'}
                </span>
                <button
                  onClick={onRefresh}
                  disabled={!onRefresh || isRefreshing}
                  className="inline-flex items-center space-x-1 px-2.5 py-1.5 text-xs rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Refresh file listing"
                >
                  <FiRotateCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 px-6 py-3 border-b border-secondary-200 bg-white">
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by filename..."
                className="flex-1 px-3 py-2 text-sm border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
              />
              <select
                value={extensionFilter}
                onChange={(e) => setExtensionFilter(e.target.value)}
                className="md:w-56 px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
              >
                <option value="all">All file types</option>
                {extensionOptions.map((extension) => (
                  <option key={extension} value={extension}>
                    {extension}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-secondary-100">
            <AnimatePresence>
              {filteredFiles.map((file, index) => (
                <motion.div
                  key={`${file.name}-${file.path || index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center px-6 py-3 hover:bg-secondary-50 cursor-pointer transition-colors ${
                    selectedFile?.name === file.name ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                  }`}
                  onClick={() => handleFileClick(file)}
                >
                  <div className="flex items-center flex-1">
                    {getFileIcon(file)}
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-secondary-800">{file.name}</div>
                      <div className="text-xs text-secondary-500">
                        {formatFileSize(file.size)} • {formatDate(file.lastModified)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(file.validationStatus)}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {filteredFiles.length === 0 && (
              <div className="px-6 py-10 text-center text-secondary-500 text-sm">
                No files match the current filter.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* File Preview Panel */}
      <AnimatePresence>
        {showPreview && selectedFile && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '50%', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white border-l border-secondary-200 flex flex-col min-h-0"
          >
            <div className="h-full flex flex-col">
              <div className="flex-shrink-0 px-6 py-4 border-b border-secondary-200 bg-secondary-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-secondary-800">{selectedFile.name}</h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="p-1 rounded-md hover:bg-secondary-100"
                  >
                    <FiX className="w-4 h-4 text-secondary-600" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                {renderFileContent()}
              </div>
              
              <div className="flex-shrink-0 px-6 py-4 border-t border-secondary-200 bg-secondary-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-secondary-500">
                    {formatFileSize(selectedFile.size)} • {formatDate(selectedFile.lastModified)}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDownloadClick}
                      title="Download file"
                      disabled={!selectedFile}
                      className="p-2 rounded-lg hover:bg-secondary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiDownload className="w-4 h-4 text-secondary-600" />
                    </button>
                    <button
                      onClick={() => onFileEdit(selectedFile)}
                      className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                      title="Edit file"
                    >
                      <FiEdit className="w-4 h-4 text-secondary-600" />
                    </button>
                    <button
                      onClick={handleDeleteClick}
                      title="Delete file"
                      disabled={!selectedFile}
                      className="p-2 rounded-lg hover:bg-secondary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiTrash2 className="w-4 h-4 text-secondary-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FileExplorer;
